// ✅ Login.jsx — enterprise-grade (single background, prominent motion particles, Get Access modal)
// - NO changes to login / forgot / google auth flow
// - Header uses hosted logo
// - Removes extra "Sign in" CTA on left
// - Adds "Get Instant Access" popup with config-driven modules + single-submit lock
// - Removes bottom pills; adds subtle left footer
// - Improves responsive (logo centered above card title)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import {
  finalizeLoginAndRedirect,
} from "./utils/authRedirect";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaLock } from "react-icons/fa";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { useNotification } from "../../context/NotificationContext";

const Login = () => {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [authError, setAuthError] = useState("");
  const [eye, setEye] = useState(true);
const [accessResult, setAccessResult] = useState(null); 
  const emailEl = useRef(null);

  const LOGO_URL =
    "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/web-assets/logo-dark.png";

  const LOGO_URL_LIGHT =
    "https://netshine-assets.blr1.cdn.digitaloceanspaces.com/web-assets/logo-light.png";

  // ---- Get Access modal state ----
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSubmitting, setAccessSubmitting] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [accessSuccess, setAccessSuccess] = useState(false);
  const [accessModules, setAccessModules] = useState([]);
  const [accessSelected, setAccessSelected] = useState([]);

const [accessStatus, setAccessStatus] = useState("idle"); 
// "idle" | "submitted" | "already_submitted"

// null | "submitted" | "already_submitted"

  
  const ACCESS_LOCK_KEY = "ns_get_access_submitted_v1";
  const accessLocked = useMemo(() => {
    const v = localStorage.getItem(ACCESS_LOCK_KEY);
    return !!v;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const appAccess = localStorage.getItem("app_access");
    if (token) {
      const parsed = appAccess ? JSON.parse(appAccess) : [];
      if (parsed?.length === 1 && parsed.includes("hms")) navigate("/hospitality");
      else navigate("/employee-dashboard");
    }
  }, [navigate]);

  const loginValidationSchema = Yup.object().shape({
    email: Yup.string().email("Please enter a valid email address").required("Email is required"),
    password: Yup.string()
      .min(4, "Password must be at least 4 characters")
      .max(20, "Password must be at most 20 characters")
      .required("Password is required"),
  });

  const forgotPasswordValidationSchema = Yup.object().shape({
    email: Yup.string().email("Please enter a valid email address").required("Email is required"),
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(isForgotPassword ? forgotPasswordValidationSchema : loginValidationSchema),
  });

  useEffect(() => {
    const details = localStorage.getItem("loginDetails");
    const loginData = details ? JSON.parse(details) : null;
    if (loginData) {
      setValue("email", loginData.email);
      setValue("password", loginData.password);
      setRememberMe(true);
    }
  }, [setValue]);

  // =============================
  // Get Instant Access: config + submit (best-effort endpoints)
  // =============================

  const accessValidationSchema = Yup.object().shape({
    path: Yup.string().oneOf(["demo", "access"]).required("Select a path"),
    name: Yup.string().min(2, "Name is too short").required("Name is required"),
    email: Yup.string().email("Please enter a valid email").required("Work email is required"),
    property: Yup.string().min(2, "Property name is too short").required("Property name is required"),
    role: Yup.string().required("Role is required"),
    phone: Yup.string().min(6, "Phone is too short").required("Phone is required"),
  });

  const {
    control: accessControl,
    handleSubmit: handleAccessSubmit,
    formState: { errors: accessErrors },
    watch: accessWatch,
    reset: accessReset,
  } = useForm({
    resolver: yupResolver(accessValidationSchema),
    defaultValues: {
      path: "access",
      name: "",
      email: "",
      property: "",
      role: "Owner",
      phone: "",
    },
  });

  const accessPath = accessWatch("path");

  const normalizeModules = (modules) => {
    if (!Array.isArray(modules)) return [];
    return modules
      .map((m) => {
        if (!m) return null;
        if (typeof m === "string") return { key: m, label: m };
        return {
          key: m.key || m.id || m.value || m.code || m.slug || m.label,
          label: m.label || m.name || m.title || m.key || m.id,
        };
      })
      .filter((m) => m?.key && m?.label);
  };

  const fetchAccessConfig = async () => {
    if (accessModules.length) return;

    setAccessLoading(true);
    try {
      // NOTE: Keep both attempts; change to your final endpoint once ready.
      const urls = [`${apiUrl}/users/get-access`, `${apiUrl}/get-access`].filter(Boolean);

      let data = null;
      for (const u of urls) {
        try {
          const r = await fetch(u, { method: "GET" });
          if (r.ok) {
            data = await r.json();
            break;
          }
        } catch (_) {
          // try next
        }
      }

      const rawModules =
        data?.modules ||
        data?.data?.modules ||
        data?.config?.modules ||
        data?.config?.products ||
        data?.products ||
        null;

      const normalized = normalizeModules(rawModules);

      setAccessModules(
        normalized.length
          ? normalized
          : [
              { key: "stayx_pms", label: "StayX PMS" },
              { key: "booking_engine", label: "Booking Engine" },
              { key: "channel_manager", label: "Channel Manager" },
              { key: "pos", label: "Nova POS" },
              { key: "website_builder", label: "Website Builder" },
              { key: "digital_menu", label: "Digital Menu" },
              { key: "guest_feedback", label: "Guest Feedback" },
              { key: "table_booking", label: "Table Booking" },
              { key: "crm", label: "CRM & Marketing" },
              { key: "revenue_ai", label: "Revenue AI" },
            ]
      );
    } finally {
      setAccessLoading(false);
    }
  };

const openAccess = () => {
  setAccessError("");
  setAccessSubmitting(false);

  // If lock exists, show "already submitted" state
  setAccessStatus(accessLocked ? "already_submitted" : "idle");

  // Reset previous selection/form on each open (keeps it clean)
  setAccessSelected([]);
  accessReset({
    path: "access",
    name: "",
    email: "",
    property: "",
    role: "Owner",
    phone: "",
  });

  setAccessOpen(true);

  // ✅ IMPORTANT: Load modules once when modal opens
  // (fetchAccessConfig already has guards to avoid duplicate calls)
  if (!accessLocked) fetchAccessConfig();
};



const closeAccess = () => {
  setAccessOpen(false);
  setAccessError("");
  setAccessSubmitting(false);
  setAccessSuccess(false);
  setAccessStatus("idle");
  setAccessSelected([]);
  accessReset({
    path: "access",
    name: "",
    email: "",
    property: "",
    role: "Owner",
    phone: "",
  });
};


  const toggleAccessModule = (key) => {
    setAccessSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

const onGetAccessSubmit = async (payload) => {
  if (accessSubmitting) return; // prevent double-submit
  if (!accessSelected.length) {
    setAccessError("Please select at least one module you are interested in.");
    return;
  }

  setAccessSubmitting(true);
  setAccessError("");

  try {
    const body = {
      path: payload.path,
      interests: accessSelected,
      name: payload.name,
      email: payload.email,
      propertyName: payload.property,
      role: payload.role,
      phone: payload.phone,
    };

    const r = await fetch(`${apiUrl}/users/get-access`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const data = await r.json().catch(() => ({}));

    if (!r.ok) {
      throw new Error(data?.message || data?.error || `Request failed (${r.status})`);
    }

    // Expected statuses: submitted | already_submitted
    const status = data?.status;

    if (status === "already_submitted") {
      setAccessStatus("already_submitted");
    } else {
      // default to submitted
      setAccessStatus("submitted");
      localStorage.setItem("ns_get_access_lock_v1", String(Date.now()));
    }

    // IMPORTANT: do NOT toast
    // notify("success", ...)  <-- remove
    // Also: clear form + selection so it can't be re-submitted from the same open modal
    setAccessSelected([]);
    accessReset({
      path: "access",
      name: "",
      email: "",
      property: "",
      role: "Owner",
      phone: "",
    });
  } catch (e) {
    setAccessError(e?.message || "Failed to submit. Please try again.");
    // notify("error", ...) <-- optional; you asked to avoid toasts for success; errors can remain toast or inline
  } finally {
    setAccessSubmitting(false);
  }
};




  // =============================
  // Google login (UNCHANGED)
  // =============================

const handleGoogleLogin = useGoogleLogin({
  onSuccess: async (tokenResponse) => {
    try {
      setAuthError("");

      const response = await axios.post(`${apiUrl}/users/google-login`, {
        googleAccessToken: tokenResponse.access_token,
      });

      if (response.status !== 200) {
        throw new Error("Google login failed.");
      }

      const d = response.data;

      finalizeLoginAndRedirect({
        responseData: d,
        navigate,
        notify,
        rememberMe: false, // usually not needed for Google login
      });
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "We couldn't find an account associated with your email. Please try a different Google account or contact your administrator.";

      setAuthError(msg);
      notify("error", msg);
    }
  },
  onError: () => {
    const msg = "Google login failed.";
    setAuthError(msg);
    notify("error", msg);
  },
});
  // =============================
  // Login submit (UNCHANGED)
  // =============================

const onSubmit = async (data) => {
  setIsLoggingIn(true);
  setAuthError("");

  try {
    const response = await fetch(`${apiUrl}/users/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: data.email,
        password: data.password,
      }),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || "Failed to login");
    }

    const d = await response.json();

    finalizeLoginAndRedirect({
      responseData: d,
      navigate,
      notify,
      rememberMe,
      email: data.email,
      password: data.password,
    });
  } catch (error) {
    setAuthError(error.message);
    notify("error", error.message);
    setIsLoggingIn(false);
  }
};

  // =============================
  // Forgot submit (UNCHANGED)
  // =============================

  const onForgotPasswordSubmit = async (data) => {
    try {
      setAuthError("");
      const response = await fetch(`${apiUrl}/users/request-password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to send reset link");
      }

      notify("success", "Reset link sent successfully");
      setIsForgotPassword(false);
    } catch (error) {
      setAuthError(error.message);
      notify("error", error.message);
    }
  };

  const year = 2026;

  const hero = {
    kickerTop: "THE INTELLIGENT CLOUD FOR",
    kickerAccent: "HOSPITALITY",
    headline: ["Run", "Hospitality", "on One Platform"],
    sub:
      "Orchestrate guest journeys, teams, and revenue signals with AI assistants and unified workflows — built for hotels and multi-property groups.",
  };

  return (
    <div className="auth-shell">
      <style>{`
        html, body { height: 100%; }
        body { margin: 0; background: #070a10; }

        /* ======================================================
           SINGLE BACKGROUND (applies to BOTH left + right)
        ====================================================== */
        .auth-shell{
          min-height: 100vh;
          width: 100%;
          display: grid;
          grid-template-columns: 1.12fr 0.88fr;
          overflow: hidden;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
          position: relative;

          /* more prominent "clouds" */
          background:
            radial-gradient(1200px 820px at 14% 18%, rgba(157,255,122,.22), rgba(0,0,0,0) 64%),
            radial-gradient(980px 700px at 74% 28%, rgba(98,240,255,.16), rgba(0,0,0,0) 62%),
            radial-gradient(860px 620px at 58% 78%, rgba(124,58,237,.14), rgba(0,0,0,0) 62%),
            linear-gradient(135deg, rgba(6,10,16,1) 0%, rgba(6,10,16,1) 46%, rgba(7,9,14,1) 100%);
        }

        /* Premium film grain */
        .auth-shell:before{
          content:"";
          position:absolute;
          inset:0;
          pointer-events:none;
          background:
            radial-gradient(circle at 20% 15%, rgba(255,255,255,.06), rgba(0,0,0,0) 42%),
            radial-gradient(circle at 82% 72%, rgba(255,255,255,.04), rgba(0,0,0,0) 48%),
            linear-gradient(rgba(255,255,255,.024), rgba(255,255,255,.024));
          mix-blend-mode: overlay;
          opacity: .42;
          z-index: 0;
        }

        /* Slow light sweep */
        .auth-shell:after{
          display:none;

          content:"";
          position:absolute;
          inset:-40%;
          pointer-events:none;
          z-index: 0;
          background:
            linear-gradient(110deg,
              rgba(0,0,0,0) 42%,
              rgba(157,255,122,.14) 52%,
              rgba(0,0,0,0) 64%);
          transform: translateX(-20%) rotate(8deg);
          animation: none;
          opacity: .62;
          filter: blur(4px);
        }

        @keyframes authSweep{
          0%   { transform: translateX(-28%) rotate(8deg); opacity:.38; }
          45%  { transform: translateX(6%) rotate(8deg); opacity:.70; }
          100% { transform: translateX(-28%) rotate(8deg); opacity:.38; }
        }

        /* ======================================================
           PARTICLES (more visible)
        ====================================================== */
        .auth-heroParticles{
          display:none;

          position:absolute;
          inset:0;
          pointer-events:none;
          z-index: 1;
          opacity: 1;
        }

        .auth-heroParticles .p{
          position:absolute;
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: rgba(157,255,122,.28);
          box-shadow: 0 0 0 12px rgba(157,255,122,.040);
          animation: authFloat 10s ease-in-out infinite;
          filter: blur(.15px);
        }
        .auth-heroParticles .p:nth-child(2n){
          width: 5px; height: 5px;
          background: rgba(98,240,255,.24);
          box-shadow: 0 0 0 14px rgba(98,240,255,.035);
          animation-duration: 12.5s;
        }
        .auth-heroParticles .p:nth-child(3n){
          width: 4px; height: 4px;
          background: rgba(255,255,255,.18);
          box-shadow: 0 0 0 16px rgba(255,255,255,.025);
          animation-duration: 14.5s;
        }

        .auth-successPanel{
  border-radius: 18px;
  padding: 22px;
  border: 1px solid rgba(157,255,122,.28);
  background: linear-gradient(180deg, rgba(157,255,122,.16), rgba(255,255,255,.02));
}

.auth-successIcon{
  font-size: 34px;
  line-height: 1;
  margin-bottom: 12px;
}

.auth-successTitle{
  font-weight: 1000;
  font-size: 16px;
  color: #0f172a;
  margin-bottom: 6px;
}

.auth-successText{
  font-weight: 750;
  font-size: 13px;
  color: rgba(15,23,42,.68);
  line-height: 1.65;
}


        @keyframes authFloat{
          0%   { transform: translate3d(0,0,0) scale(1); opacity: .55; }
          35%  { transform: translate3d(20px,-28px,0) scale(1.10); opacity: .98; }
          70%  { transform: translate3d(-12px,-20px,0) scale(1.04); opacity: .82; }
          100% { transform: translate3d(0,0,0) scale(1); opacity: .60; }
        }

        .auth-heroParticles .p.p1{ left: 8%; top: 14%; animation-delay: -2s; }
        .auth-heroParticles .p.p2{ left: 18%; top: 42%; animation-delay: -7s; }
        .auth-heroParticles .p.p3{ left: 30%; top: 72%; animation-delay: -4s; }
        .auth-heroParticles .p.p4{ left: 44%; top: 18%; animation-delay: -9s; }
        .auth-heroParticles .p.p5{ left: 56%; top: 48%; animation-delay: -6s; }
        .auth-heroParticles .p.p6{ left: 64%; top: 78%; animation-delay: -3s; }
        .auth-heroParticles .p.p7{ left: 76%; top: 22%; animation-delay: -10s; }
        .auth-heroParticles .p.p8{ left: 86%; top: 54%; animation-delay: -5s; }
        .auth-heroParticles .p.p9{ left: 90%; top: 18%; animation-delay: -8s; }
        .auth-heroParticles .p.p10{ left: 12%; top: 86%; animation-delay: -1s; }

        /* ======================================================
           LEFT
        ====================================================== */
        .auth-left{
          position: relative;
          padding: clamp(20px, 2.2vw, 30px);
          display: flex;
          flex-direction: column;
          gap: 16px;
          z-index: 2;
        }

        .auth-topStrip{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 14px;
          padding: 10px 6px;
        }

        .auth-logo{
          width: 176px;
          height: auto;
          display:block;
          filter: drop-shadow(0 18px 54px rgba(0,0,0,.48));
        }

        .auth-hero{
          margin: auto 0;
          max-width: 900px;
          padding: 10px 2px 0;
        }

        .auth-kicker{
          display:inline-flex;
          align-items:center;
          gap: 10px;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.12);
          backdrop-filter: blur(12px);
          color: rgba(255,255,255,.84);
          font-weight: 950;
          font-size: 12px;
          letter-spacing: .14em;
          text-transform: uppercase;
          width: fit-content;
          margin-bottom: 16px;
        }

        /* Smooth pulse dot (no jerk) */
        .auth-dot{
          width: 9px; height: 9px; border-radius: 999px;
          background: linear-gradient(135deg, rgba(157,255,122,1), rgba(98,240,255,1));
          box-shadow: 0 0 0 0 rgba(157,255,122,.20);
          animation: authPulse 1.9s cubic-bezier(.22,.61,.36,1) infinite;
        }
        @keyframes authPulse{
          0%   { transform: scale(1); opacity: .85; box-shadow: 0 0 0 0 rgba(157,255,122,.18); }
          60%  { transform: scale(1.12); opacity: 1; box-shadow: 0 0 0 12px rgba(157,255,122,.00); }
          100% { transform: scale(1); opacity: .88; box-shadow: 0 0 0 0 rgba(157,255,122,.00); }
        }

        .auth-caption{
          margin: 0 0 18px;
          color: rgba(255,255,255,.80);
          font-weight: 950;
          font-size: 12px;
          letter-spacing: .18em;
          text-transform: uppercase;
          display:flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: baseline;
        }
        .auth-captionAccent{
          color: #9DFF7A;
          position: relative;
          display: inline-block;
          padding-bottom: 2px;
        }
        .auth-captionAccent:after{
          content:"";
          position:absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 2px;
          border-radius: 999px;
          background: linear-gradient(90deg, rgba(157,255,122,.92), rgba(98,240,255,.55));
          opacity: .90;
          transform-origin: left center;
          transform: scaleX(0);
          animation: authUnderlineDraw 2.8s ease-in-out infinite;
        }
        @keyframes authUnderlineDraw{
          0%   { transform: scaleX(0); opacity: .0; }
          18%  { transform: scaleX(1); opacity: .92; }
          72%  { transform: scaleX(1); opacity: .92; }
          100% { transform: scaleX(1); opacity: .0; }
        }

        .auth-h1{
          margin: 0 0 22px; /* more spacing before description */
          font-weight: 1000;
          letter-spacing: -0.052em;
          line-height: 1.02;
          color: rgba(255,255,255,.96);
          font-size: clamp(44px, 4.4vw, 78px);
          text-shadow: 0 22px 70px rgba(0,0,0,.62);

          font-size: clamp(3.4rem, 5.6vw, 4.6rem);
  line-height: 1.04;
  letter-spacing: -0.6px;
  margin: 0 0 22px 0;
        }

        .auth-accent{
          color: #9DFF7A;
          text-shadow: 0 14px 50px rgba(157,255,122,.16);
          position: relative;
          display: inline-block;
        }
        .auth-accent::after{
          content:"";
          position:absolute;
          left: 0;
          right: 0;
          bottom: -0.16em;
          height: .18em;
          border-radius: 999px;
          background: linear-gradient(90deg, rgba(157,255,122,.85), rgba(98,240,255,.70), rgba(157,255,122,.85));
          opacity: .20;
          filter: blur(1px);
          background-size: 180% 100%;
          animation: none;
        }
        @keyframes authShimmer{
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .auth-sub{
          max-width: 700px;
          margin: 0;
          color: rgba(255,255,255,.78);
          font-size: 15px;
          line-height: 1.95;
          padding: 12px 14px;
          border-radius: 16px;
          background: rgba(255,255,255,.05);
          border: 1px solid rgba(255,255,255,.10);
          backdrop-filter: blur(12px);
        }

        .auth-heroActions{
          margin-top: 18px;
          display:flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .auth-ctaGreen{
          border-radius: 14px;
          padding: 12px 14px;
          font-weight: 1000;
          cursor: pointer;
          border: 1px solid rgba(157,255,122,.30);
          background: linear-gradient(135deg, rgba(157,255,122,.92), rgba(52,211,153,.86));
          color: rgba(10,14,20,.94);
          box-shadow: 0 18px 50px rgba(52,211,153,.18);
          transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
        }
        .auth-ctaGreen:hover{
          transform: translateY(-1px);
          filter: brightness(1.03);
          box-shadow: 0 22px 62px rgba(52,211,153,.24);
        }

        .auth-leftFooter{
          margin-top: auto;
          padding: 10px 6px 0;
          color: rgba(255,255,255,.58);
          font-size: 12px;
          font-weight: 800;
        }
        .auth-leftFooter .pill{
          width: fit-content;
          padding: 10px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.045);
          border: 1px solid rgba(255,255,255,.10);
          backdrop-filter: blur(10px);
        }

        /* ======================================================
           RIGHT (on same background)
        ====================================================== */
        .auth-right{
          position: relative;
          display: grid;
          place-items: center;
          padding: clamp(16px, 2vw, 26px);
          z-index: 2;
        }

        .auth-right:before{
          content:"";
          position:absolute;
          inset: 10% 7%;
          border-radius: 44px;
          background:
            radial-gradient(circle at 30% 30%, rgba(255,255,255,.16), rgba(0,0,0,0) 60%),
            radial-gradient(circle at 70% 70%, rgba(98,240,255,.10), rgba(0,0,0,0) 58%);
          filter: blur(18px);
          opacity: .80;
          pointer-events:none;
        }

        .auth-card{
          width: min(520px, 94%);
          border-radius: 28px;
          background: linear-gradient(180deg, rgba(255,255,255,.96) 0%, rgba(244,246,252,.92) 100%);
          border: 1px solid rgba(255,255,255,.28);
          box-shadow:
            0 34px 120px rgba(0,0,0,.38),
            0 1px 0 rgba(255,255,255,.40) inset;
          backdrop-filter: blur(16px);
          overflow: hidden;
          transform: translateZ(0);
        }

        .auth-cardInner{ padding: 26px 26px 16px; }

        .auth-cardLogo{
          display:none;
          justify-content:center;
          margin-bottom: 12px;
        }
        .auth-cardLogo img{
          width: 168px;
          height: auto;
        }

        .auth-title{
          display:flex;
          align-items:center;
          gap: 10px;
          font-size: 20px;
          font-weight: 1000;
          color: #141821;
          margin: 0 0 6px;
        }

        .auth-note{
          margin: 0 0 16px;
          color: rgba(20,24,30,.62);
          font-weight: 750;
          font-size: 13px;
          line-height: 1.55;
        }

        .auth-errorBox{
          background: rgba(220,53,69,.08);
          border: 1px solid rgba(220,53,69,.22);
          padding: 10px 12px;
          border-radius: 14px;
          margin-bottom: 14px;
          font-weight: 700;
        }

        .auth-googleBtn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap: 10px;
          width: 100%;
          border-radius: 14px;
          font-size: 14px;
          font-weight: 950;
          color: #111827;
          background: rgba(255,255,255,.98);
          border: 1px solid rgba(0,0,0,.10);
          padding: 12px 12px;
          transition: transform .12s ease, box-shadow .12s ease;
        }
        .auth-googleBtn:hover{
          transform: translateY(-1px);
          box-shadow: 0 10px 24px rgba(0,0,0,.10);
        }

        .auth-sep{
          display:flex;
          align-items:center;
          gap: 12px;
          margin: 16px 0;
        }
        .auth-sepLine{
          flex: 1;
          border: none;
          height: 1px;
          background: rgba(0,0,0,.10);
          margin: 0;
        }
        .auth-sepText{
          font-size: 12px;
          font-weight: 1000;
          letter-spacing: .10em;
          text-transform: uppercase;
          color: rgba(20,24,30,.50);
        }

        .auth-inputBlock{ margin-bottom: 1.05em; }

        .auth-label{
          font-weight: 950;
          color: rgba(20,24,30,.76);
          font-size: 13px;
          margin-bottom: 6px;
          display: block;
        }

        .auth-inputGroupText{
          background: rgba(0,0,0,.03) !important;
          border-color: rgba(0,0,0,.12) !important;
          border-radius: 12px 0 0 12px !important;
        }

        .auth-input{
          border-color: rgba(0,0,0,.12) !important;
          border-radius: 0 12px 12px 0 !important;
          padding: 12px 12px !important;
          background: rgba(255,255,255,.98) !important;
        }

        .auth-input:focus{
          border-color: rgba(79,70,229,.55) !important;
          box-shadow: 0 0 0 4px rgba(79,70,229,.14) !important;
        }

        .auth-errorInput{
          border-color: rgba(220,53,69,.65) !important;
          box-shadow: 0 0 0 4px rgba(220,53,69,.12) !important;
        }

        .auth-focusPulse{
          box-shadow: 0 0 0 5px rgba(157,255,122,.18) !important;
        }

        .auth-primaryBtn{
          width: 100%;
          border-radius: 14px !important;
          font-weight: 1000 !important;
          padding: 12px 14px !important;
          border: none !important;
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%) !important;
          box-shadow: 0 18px 48px rgba(79,70,229,.22) !important;
          transition: transform .12s ease, box-shadow .12s ease;
        }
        .auth-primaryBtn:hover{
          transform: translateY(-1px);
          box-shadow: 0 24px 70px rgba(79,70,229,.26) !important;
        }
        .auth-primaryBtn:disabled{
          opacity: .72;
          transform: none;
          box-shadow: 0 16px 40px rgba(79,70,229,.16) !important;
          cursor: not-allowed;
        }

        .auth-spinner{
          font-size: 16px;
          vertical-align: middle;
          color: #fff;
          margin-right: 8px;
        }

        .auth-footNote{
          padding: 14px 26px 18px;
          border-top: 1px solid rgba(0,0,0,.06);
          color: rgba(20,24,30,.56);
          font-size: 12px;
          font-weight: 700;
          line-height: 1.6;
          text-align: center;
          background: rgba(255,255,255,.78);
        }

     /* ======================================================
   GET ACCESS MODAL (Enterprise, sticky header/footer)
   - Overlay z-index 9999
   - Modal uses grid rows: header / scroll body / footer
   - Footer buttons left aligned
====================================================== */

/* Overlay */
.auth-modalBack{
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  padding: 18px;
  background: rgba(3, 6, 12, .66);
  backdrop-filter: blur(10px);
}

/* Modal shell */
.auth-modal{
  width: min(760px, 96vw);
  max-height: min(86vh, 860px);
  border-radius: 22px;
  overflow: hidden;

  display: grid;
  grid-template-rows: auto 1fr auto;

  background: linear-gradient(180deg, rgba(255,255,255,.97), rgba(255,255,255,.92));
  border: 1px solid rgba(255,255,255,.22);
  box-shadow: 0 32px 120px rgba(0,0,0,.55);
  transform: translateZ(0);
}

/* Sticky header */
.auth-modalHead{
  position: sticky;
  top: 0;
  z-index: 5;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 16px 18px;

  background: linear-gradient(180deg, rgba(255,255,255,.99), rgba(255,255,255,.88));
  border-bottom: 1px solid rgba(0,0,0,.08);
  backdrop-filter: blur(10px);
}

.auth-modalTitle{
  margin: 0;
  font-size: 16px;
  font-weight: 1000;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.auth-modalClose{
  border: 1px solid rgba(0,0,0,.10);
  background: rgba(255,255,255,.92);
  width: 36px;
  height: 36px;
  border-radius: 12px;
  font-weight: 1000;
  cursor: pointer;
  display: grid;
  place-items: center;
}
.auth-modalClose:hover{ filter: brightness(.98); }

/* Scroll body */
.auth-modalBody{
  overflow: auto;
  padding: 16px 18px 18px;
  background: transparent;
}

/* Better scrollbar */
.auth-modalBody::-webkit-scrollbar{ width: 10px; }
.auth-modalBody::-webkit-scrollbar-thumb{
  background: rgba(17,24,39,.18);
  border-radius: 999px;
  border: 3px solid rgba(255,255,255,0);
  background-clip: padding-box;
}

/* Sticky footer */
.auth-modalFooter{
  position: sticky;
  bottom: 0;
  z-index: 5;

  display: flex;
  gap: 10px;
  justify-content: flex-start; /* LEFT aligned */
  align-items: center;

  padding: 14px 18px;

  background: linear-gradient(180deg, rgba(255,255,255,.80), rgba(255,255,255,.96));
  border-top: 1px solid rgba(0,0,0,.08);
  backdrop-filter: blur(10px);
}

/* Footer buttons */
.auth-btnGhost{
  border-radius: 12px;
  padding: 11px 14px;
  font-weight: 950;
  background: rgba(15,23,42,.06);
  border: 1px solid rgba(15,23,42,.12);
  color: rgba(15,23,42,.88);
  cursor: pointer;
}
.auth-btnGhost:hover{ background: rgba(15,23,42,.08); }

.auth-btnPrimary{
  border-radius: 12px;
  padding: 11px 16px;
  font-weight: 1000;
  border: none;
  color: #0b1220;
  background: linear-gradient(135deg, rgba(157,255,122,.96), rgba(52,211,153,.88));
  box-shadow: 0 18px 44px rgba(52,211,153,.18);
  cursor: pointer;
}
.auth-btnPrimary:hover{
  filter: brightness(1.02);
  box-shadow: 0 22px 58px rgba(52,211,153,.22);
}
.auth-btnPrimary:disabled,
.auth-btnGhost:disabled{
  opacity: .7;
  cursor: not-allowed;
}

/* Thank you box */
.auth-thanks{
  margin-top: 10px;
  border-radius: 16px;
  padding: 14px;
  border: 1px solid rgba(157,255,122,.35);
  background: rgba(157,255,122,.12);
  font-weight: 900;
  color: rgba(10,14,20,.86);
  line-height: 1.5;
}

/* Path buttons */
.auth-pathRow{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}
.auth-pathBtn{
  border-radius: 14px;
  padding: 12px 12px;
  font-weight: 1000;
  border: 1px solid rgba(0,0,0,.12);
  background: rgba(255,255,255,.72);
  cursor: pointer;
}
.auth-pathBtn.isActive{
  border-color: rgba(157,255,122,.40);
  background: rgba(157,255,122,.16);
}

/* Section label */
.auth-sectionLabel{
  font-size: 11px;
  font-weight: 1000;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: rgba(15,23,42,.58);
  margin: 10px 0 10px;
}

/* Chips */
.auth-chips{
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.auth-chip{
  user-select: none;
  border-radius: 999px;
  padding: 9px 12px;
  font-weight: 950;
  font-size: 12px;
  border: 1px solid rgba(0,0,0,.12);
  background: rgba(255,255,255,.75);
  color: rgba(15,23,42,.86);
  cursor: pointer;
  transition: transform .12s ease, background .12s ease, border-color .12s ease;
}
.auth-chip:hover{ transform: translateY(-1px); }
.auth-chip.isOn{
  border-color: rgba(157,255,122,.42);
  background: rgba(157,255,122,.18);
}

/* Form layout inside modal */
.auth-grid2{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 14px;
  margin-top: 10px;
}
.auth-grid2 .full{ grid-column: 1 / -1; }

/* Inputs */
.auth-modalInput{
  width: 100%;
  border-radius: 12px;
  padding: 12px 12px;
  border: 1px solid rgba(0,0,0,.12);
  background: rgba(255,255,255,.96);
  outline: none;
}
.auth-modalInput:focus{
  border-color: rgba(79,70,229,.45);
  box-shadow: 0 0 0 4px rgba(79,70,229,.12);
}

/* Errors */
.auth-modalErr{
  margin-top: 6px;
  font-weight: 800;
  color: rgba(220,53,69,.92);
  font-size: 12px;
}

/* Responsive modal */
@media (max-width: 640px){
  .auth-modalBack{ padding: 12px; }
  .auth-modal{ width: 100%; max-height: 88vh; border-radius: 18px; }
  .auth-pathRow{ grid-template-columns: 1fr; }
  .auth-grid2{ grid-template-columns: 1fr; }
}
/* ======================================================
   RESPONSIVE RESTORE (PAGE)
   Put this at the VERY END so it overrides earlier rules
====================================================== */

@media (max-width: 991px){
  /* 1-col layout */
  .auth-shell{
    grid-template-columns: 1fr !important;
  }

  /* order: login first */
  .auth-right{
    order: 1 !important;
    padding: 14px !important;
  }
  .auth-left{
    order: 2 !important;
    padding: 16px !important;
  }

  /* Left heading sizing */
  .auth-h1{
    font-size: clamp(30px, 7vw, 46px) !important;
    margin-bottom: 16px !important;
  }
  .auth-sub{
    max-width: 100% !important;
  }

  /* We do NOT want logo under text on mobile */
  .auth-topStrip{
    display: none !important; /* hides left logo row completely */
  }

  /* Show logo inside the white card on mobile */
  .auth-cardLogo{
    display: flex !important;
  }
  .auth-title{
    justify-content: center !important;
    text-align: center !important;
  }
  .auth-note{
    text-align: center !important;
  }

  /* Optional: make right card full-width feel nicer */
  .auth-card{
    width: min(560px, 96%) !important;
  }

  /* Right glow tighter */
  .auth-right:before{
    inset: 18% 8% !important;
  }

  /* Modal form becomes 1-col on mobile (already in your 640px, but keep safe) */
  .auth-grid2{
    grid-template-columns: 1fr !important;
  }
}
      `}</style>

      {/* PARTICLES */}
      <div className="auth-heroParticles" aria-hidden="true">
        <span className="p p1" />
        <span className="p p2" />
        <span className="p p3" />
        <span className="p p4" />
        <span className="p p5" />
        <span className="p p6" />
        <span className="p p7" />
        <span className="p p8" />
        <span className="p p9" />
        <span className="p p10" />
      </div>

      {/* LEFT */}
      <div className="auth-left">
        <div className="auth-topStrip">
          <img className="auth-logo" src={LOGO_URL} alt="NetShine" />
        </div>

        <div className="auth-hero">
          <div className="auth-kicker">
            <span className="auth-dot" aria-hidden="true" />
     
          </div>

          <h1 className="auth-h1">
        {hero.kickerTop} <span className="auth-captionAccent">{hero.kickerAccent}</span>
          </h1>

          <p className="auth-sub">{hero.sub}</p>

          <div className="auth-heroActions">
            <button type="button" className="auth-ctaGreen" onClick={openAccess}>
              Get Instant Access →
            </button>
          </div>
        </div>

        <div className="auth-leftFooter">
          <div className="pill"><a className="auth-footerLink" href="https://netshine.one" target="_blank" rel="noreferrer">NetShine ONE</a> — a product of NetShine Technologies © {year}</div>
        </div>
      </div>

      {/* RIGHT */}
      <div className="auth-right">
        <div className="auth-card">
          <div className="auth-cardInner">
            <div className="auth-cardLogo">
              <img src={LOGO_URL_LIGHT} alt="NetShine" />
            </div>

            <div className="auth-title">
              <FaLock />
              {isForgotPassword ? "Password Recovery" : "Access Workspace"}
            </div>
            <div className="auth-note">
              {isForgotPassword
                ? "Enter your email to receive a secure reset link."
                : "Sign in with Google or use your work email and password."}
            </div>

            {!!authError && <div className="auth-errorBox text-danger">{authError}</div>}

            {isForgotPassword ? (
              <form onSubmit={handleSubmit(onForgotPasswordSubmit)}>
                <div className="auth-inputBlock">
                  <label className="auth-label">Email Address</label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <div className="input-group">
                        <span className="input-group-text auth-inputGroupText">
                          <i className="fa fa-envelope" />
                        </span>
                        <input
                          className={`form-control auth-input ${errors?.email ? "auth-errorInput" : ""}`}
                          type="text"
                          placeholder="Enter your email"
                          {...field}
                          autoComplete="email"
                        />
                      </div>
                    )}
                  />
                  <span className="text-danger">{errors.email?.message}</span>
                </div>

                <button className="btn btn-primary auth-primaryBtn" type="submit">
                  Send Reset Link
                </button>

                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => setIsForgotPassword(false)}
                    style={{ fontWeight: 900, padding: 0 }}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <button type="button" className="auth-googleBtn" onClick={handleGoogleLogin}>
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    version="1.1"
                    x="0px"
                    y="0px"
                    viewBox="0 0 48 48"
                    enableBackground="new 0 0 48 48"
                    style={{ width: 20, height: 20 }}
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      fill="#FFC107"
                      d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24 c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                    />
                    <path
                      fill="#FF3D00"
                      d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                    />
                    <path
                      fill="#4CAF50"
                      d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                    />
                    <path
                      fill="#1976D2"
                      d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                    />
                  </svg>
                  Sign in with Google
                </button>

                <div className="auth-sep">
                  <hr className="auth-sepLine" />
                  <span className="auth-sepText">Or</span>
                  <hr className="auth-sepLine" />
                </div>

                <div className="auth-inputBlock">
                  <label className="auth-label">Email Address</label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <div className="input-group">
                        <span className="input-group-text auth-inputGroupText">
                          <i className="fa fa-envelope" />
                        </span>
                        <input
                          className={`form-control auth-input ${errors?.email ? "auth-errorInput" : ""}`}
                          type="text"
                          placeholder="Enter your email"
                          {...field}
                          ref={(el) => {
                            field.ref(el);
                            emailEl.current = el;
                          }}
                          autoComplete="email"
                        />
                      </div>
                    )}
                  />
                  <span className="text-danger">{errors.email?.message}</span>
                </div>

                <div className="auth-inputBlock">
                  <div className="row">
                    <div className="col">
                      <label className="auth-label">Password</label>
                    </div>
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={() => setIsForgotPassword(true)}
                        style={{ fontWeight: 900, padding: 0 }}
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>

                  <div className="input-group" style={{ position: "relative" }}>
                    <span className="input-group-text auth-inputGroupText">
                      <i className="fa fa-lock" />
                    </span>

                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <input
                          className={`form-control auth-input ${errors?.password ? "auth-errorInput" : ""}`}
                          type={eye ? "password" : "text"}
                          placeholder="Enter your password"
                          {...field}
                          autoComplete="current-password"
                        />
                      )}
                    />

                    <span
                      style={{ position: "absolute", right: 12, top: 12, cursor: "pointer" }}
                      onClick={() => setEye((v) => !v)}
                      className={`fa-solid ${eye ? "fa-eye-slash" : "fa-eye"}`}
                      aria-label={eye ? "Show password" : "Hide password"}
                      role="button"
                      tabIndex={0}
                    />
                  </div>

                  <span className="text-danger">{errors.password?.message}</span>
                </div>

                <button className="btn btn-primary auth-primaryBtn" type="submit" disabled={isLoggingIn}>
                  {isLoggingIn ? (
                    <>
                      <i className="fa fa-spinner fa-spin auth-spinner" />
                      Access Now
                    </>
                  ) : (
                    "Access Now"
                  )}
                </button>

                <div className="form-check mb-3" style={{ marginTop: 12 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  <label className="form-check-label" htmlFor="rememberMe">
                    Remember Me
                  </label>
                </div>
              </form>
            )}
          </div>

          <div className="auth-footNote">
            By continuing, you agree to your organization’s usage policy and security guidelines.
          </div>
        </div>
      </div>

      {/* GET ACCESS MODAL */}
      {accessOpen && (
        <div className="auth-modalBack" role="dialog" aria-modal="true" aria-label="Get Instant Access">
          <div className="auth-modal">
            <div className="auth-modalHead">
              <h3 className="auth-modalTitle">Get Instant Access</h3>
              <button type="button" className="auth-modalClose" onClick={closeAccess} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="auth-modalBody">
{accessStatus === "submitted" || accessStatus === "already_submitted" ? (

  <div className="auth-successPanel">
    <div className="auth-successIcon">
      {accessStatus === "already_submitted" ? "👋" : "👍"}
    </div>

    <div className="auth-successTitle">
      {accessStatus === "already_submitted"
        ? "You’ve already requested access"
        : "Thank you for your request"}
    </div>

    <div className="auth-successText">
      {accessStatus === "already_submitted"
        ? "We already have your request in our system. A NetShine specialist will connect with you soon."
        : "We’ve received your details successfully. We’ll get back to you shortly with next steps."}
    </div>
  </div>
) : (
                <>
                  <div className="auth-pathRow">
                    <button
                      type="button"
                      className={`auth-pathBtn ${accessPath === "demo" ? "isActive" : ""}`}
                      onClick={() => {
                        // update react-hook-form without extra deps
                        // eslint-disable-next-line no-underscore-dangle
                        accessReset({ ...accessWatch(), path: "demo" });
                      }}
                    >
                      Book Strategy Demo
                    </button>

                    <button
                      type="button"
                      className={`auth-pathBtn ${accessPath === "access" ? "isActive" : ""}`}
                      onClick={() => {
                        // eslint-disable-next-line no-underscore-dangle
                        accessReset({ ...accessWatch(), path: "access" });
                      }}
                    >
                      Get Free Access
                    </button>
                  </div>

                  <div className="auth-sectionLabel">I'M INTERESTED IN (SELECT MULTIPLE)</div>

                  {accessLoading ? (
                    <div style={{ fontWeight: 900, color: "rgba(17,24,39,.65)" }}>Loading…</div>
                  ) : (
                    <div className="auth-chips">
                      {accessModules.map((m) => (
                        <span
                          key={m.key}
                          className={`auth-chip ${accessSelected.includes(m.key) ? "isOn" : ""}`}
                          onClick={() => toggleAccessModule(m.key)}
                          role="button"
                          tabIndex={0}
                        >
                          {m.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {!!accessError && <div className="auth-modalErr" style={{ marginTop: 10 }}>{accessError}</div>}

                  <form id="authAccessForm" onSubmit={handleAccessSubmit(onGetAccessSubmit)}>
                    <div className="auth-grid2">
                      <div className="full">
                        <label className="auth-label">Your Name</label>
                        <Controller
                          name="name"
                          control={accessControl}
                          render={({ field }) => (
                            <input
                              className="auth-modalInput"
                              placeholder="e.g. Sarah Jenkins"
                              autoComplete="name"
                              {...field}
                            />
                          )}
                        />
                        {!!accessErrors?.name && <div className="auth-modalErr">{accessErrors.name.message}</div>}
                      </div>

                      <div className="full">
                        <label className="auth-label">Work Email</label>
                        <Controller
                          name="email"
                          control={accessControl}
                          render={({ field }) => (
                            <input
                              className="auth-modalInput"
                              placeholder="name@hotel.com"
                              autoComplete="email"
                              {...field}
                            />
                          )}
                        />
                        {!!accessErrors?.email && <div className="auth-modalErr">{accessErrors.email.message}</div>}
                      </div>

                      <div>
                        <label className="auth-label">Property Name</label>
                        <Controller
                          name="property"
                          control={accessControl}
                          render={({ field }) => (
                            <input className="auth-modalInput" placeholder="Aurora Resort" {...field} />
                          )}
                        />
                        {!!accessErrors?.property && <div className="auth-modalErr">{accessErrors.property.message}</div>}
                      </div>

                      <div>
                        <label className="auth-label">Role</label>
                        <Controller
                          name="role"
                          control={accessControl}
                          render={({ field }) => (
                            <select className="auth-modalInput" {...field}>
                              <option value="Owner">Owner</option>
                              <option value="GM">General Manager</option>
                              <option value="Revenue">Revenue / Sales</option>
                              <option value="FrontDesk">Front Desk</option>
                              <option value="IT">IT / Operations</option>
                              <option value="Other">Other</option>
                            </select>
                          )}
                        />
                        {!!accessErrors?.role && <div className="auth-modalErr">{accessErrors.role.message}</div>}
                      </div>

                      <div className="full">
                        <label className="auth-label">Phone</label>
                        <Controller
                          name="phone"
                          control={accessControl}
                          render={({ field }) => (
                            <input className="auth-modalInput" placeholder="+91…" autoComplete="tel" {...field} />
                          )}
                        />
                        {!!accessErrors?.phone && <div className="auth-modalErr">{accessErrors.phone.message}</div>}
                      </div>
                    </div>
                  </form>
                </>
              )}
            </div>

            <div className="auth-modalFooter">
              <button type="button" className="auth-btnGhost" onClick={closeAccess} disabled={accessSubmitting}>
                Cancel
              </button>
              <button
                type="submit"
                form="authAccessForm"
                className="auth-btnPrimary"
                disabled={accessSubmitting || accessLoading || accessSuccess}
              >
                {accessSubmitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
